// Infinite-Scroll-Fetch-Hook für die Library (`GET /api/assets`, Keyset-Cursor).
//
// Ownership (B3): lebt in LibraryPage — NICHT in AssetGrid/AssetTable, sonst
// würde der View-Wechsel (Grid↔Liste) from-scratch refetchen. Der Accumulator
// (`assets`) ist die Single-Source der geladenen, geordneten Asset-Liste und
// speist Selection (`allIds`) + J/K-Bridge (`saveLibraryAssetIds`).
//
// Reset-Trigger (B4): jeder `queryString`-Wechsel (Filter/Sort/Status/Tag/…)
// UND `refetchKey`-Bump (Upload/Bulk via ASSETS_CHANGED) → Accumulator + Cursor
// + Scroll auf null, erste Seite neu laden.
//
// Doppel-Fetch-Defense (C2): `loadingMore`-State allein reicht NICHT — der
// Virtualizer-Callback feuert mehrfach im selben Microtask VOR dem State-Flip.
// Darum ein SYNCHRONER `inFlightCursor`-Ref (gesetzt vor dem fetch) als Guard +
// ID-Dedupe beim Append als zweite Verteidigung. `generation` invalidiert
// stale Responses nach einem Reset (ersetzt das `cancelled`-Flag).
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import type { AssetListItem } from './asset-types';
import { useScrollContainerRef } from './layout-context';

type AssetsPage = { assets: AssetListItem[]; nextCursor: string | null };

export type InfiniteAssets = {
  /** Accumulator — alle bisher geladenen Assets in stabiler Sortreihenfolge. */
  assets: AssetListItem[];
  /** Status der ERSTEN Seite (Skeleton/Empty/Error). Nachlade-Fehler separat. */
  status: 'loading' | 'error' | 'ok';
  /** Fehlermeldung der ersten Seite (nur bei status==='error'). */
  errorMessage: string | null;
  /** Gibt es weitere Seiten? (nextCursor !== null) */
  hasMore: boolean;
  /** Lädt gerade eine Folgeseite nach. */
  loadingMore: boolean;
  /** Fehler beim Nachladen — bestehende Liste bleibt, inline-Retry möglich. */
  loadMoreError: string | null;
  /** Nächste Seite laden (idempotent; vom Virtualizer-Prefetch + Retry gerufen). */
  loadMore: () => void;
};

/**
 * @param queryString  Die Filter-/Sort-Query OHNE `cursor` — entweder leer (`''`)
 *                     oder mit führendem `?` (z.B. `?type=img&sort=name&dir=asc`).
 *                     MUSS ein stabiler String-Dep sein (kein neues Objekt/Array
 *                     pro Render), sonst Refetch-Loop.
 * @param refetchKey   Monoton steigender Zähler; Bump erzwingt einen Reset.
 */
export function useInfiniteAssets(queryString: string, refetchKey: number): InfiniteAssets {
  const scrollRef = useScrollContainerRef();
  const [assets, setAssets] = useState<AssetListItem[]>([]);
  const [status, setStatus] = useState<'loading' | 'error' | 'ok'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  // Synchrone Refs (überleben Re-Render ohne Re-Trigger):
  const loadedIds = useRef<Set<string>>(new Set()); // ID-Dedupe beim Append (C2)
  const inFlightCursor = useRef<string | null>(null); // gerade gefetchter Cursor (C2)
  const cursorRef = useRef<string | null>(null); // synchroner Spiegel von nextCursor
  const generation = useRef(0); // invalidiert stale Responses nach Reset

  // ── Reset + erste Seite (B4) ───────────────────────────────────────────
  useEffect(() => {
    const gen = ++generation.current;
    loadedIds.current = new Set();
    inFlightCursor.current = null;
    cursorRef.current = null;
    setAssets([]);
    setNextCursor(null);
    setLoadingMore(false);
    setLoadMoreError(null);
    setStatus('loading');
    setErrorMessage(null);
    // Scroll-Reset auf den App-Scroll-Container (<main>).
    if (scrollRef.current) scrollRef.current.scrollTop = 0;

    (async () => {
      try {
        const res = await api<AssetsPage>(`/api/assets${queryString}`);
        if (gen !== generation.current) return; // ein Reset kam dazwischen
        loadedIds.current = new Set(res.assets.map((a) => a.id));
        setAssets(res.assets);
        cursorRef.current = res.nextCursor;
        setNextCursor(res.nextCursor);
        setStatus('ok');
      } catch (err) {
        if (gen !== generation.current) return;
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
      }
    })();
    // scrollRef ist ein stabiles Ref-Objekt (Context) → kein Dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString, refetchKey]);

  // ── Folgeseite nachladen (C1-Prefetch ruft das; C2-Guards) ─────────────
  const loadMore = useCallback(() => {
    const cursor = cursorRef.current;
    if (!cursor) return; // letzte Seite erreicht
    if (inFlightCursor.current === cursor) return; // synchroner Doppel-Fetch-Guard (C2)
    inFlightCursor.current = cursor;
    const gen = generation.current;
    setLoadingMore(true);
    setLoadMoreError(null);

    (async () => {
      try {
        const sep = queryString ? '&' : '?';
        const url = `/api/assets${queryString}${sep}cursor=${encodeURIComponent(cursor)}`;
        const res = await api<AssetsPage>(url);
        if (gen !== generation.current) return; // Reset während des Fetches
        // ID-Dedupe (C2, 2. Verteidigung) — Insert/Delete zwischen den Seiten
        // kann sonst ein Asset doppelt liefern.
        const incoming = res.assets.filter((a) => !loadedIds.current.has(a.id));
        for (const a of incoming) loadedIds.current.add(a.id);
        if (incoming.length > 0) setAssets((prev) => [...prev, ...incoming]);
        cursorRef.current = res.nextCursor;
        setNextCursor(res.nextCursor);
      } catch (err) {
        if (gen !== generation.current) return;
        setLoadMoreError(err instanceof Error ? err.message : 'Nachladen fehlgeschlagen');
      } finally {
        if (gen === generation.current) setLoadingMore(false);
        // Guard für DIESEN Cursor freigeben (Retry / nächste Seite möglich).
        if (inFlightCursor.current === cursor) inFlightCursor.current = null;
      }
    })();
  }, [queryString]);

  return {
    assets,
    status,
    errorMessage,
    hasMore: nextCursor !== null,
    loadingMore,
    loadMoreError,
    loadMore,
  };
}
